import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { Registration } from '@/types/registration.types';
import Container from '@ui/container/container';
import SwitchV2 from '@ui/switch-v2/switch-v2';
import Text from '@ui/text/text/text';
import useComputedRegistrationSettings from '../../hooks/use-computed-registration-settings';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import OptionalComponent from '@ui/optional-component/optional-component';
import { validateUrl } from '@/utils/validate-url';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import { ChangeEvent, useState } from 'react';

export default function DisableNativeRegistrationToggle() {
  const dispatch = useAppDispatch();
  const { registrationSettings } = useComputedRegistrationSettings();
  const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);

  const disableNativeRegistrationIsOn = registrationSettings?.externalRegistration?.isOn;
  const [externalUrl, setExternalUrl] = useState<string | undefined | null>(
    registrationSettings?.externalRegistration?.url
  );

  const toggleDisableNativeRegistration = (value: boolean, on: boolean) => {
    if (!registrationSettings) return;
    const updatedSettings: Registration = {
      ...registrationSettings,
      externalRegistration: {
        ...registrationSettings?.externalRegistration,
        isOn: on,
      },
    };

    dispatch(saveWorkingProjectByFields({ registration_settings: updatedSettings }));
  };

  const setExernalUrl = (value: string | null) => {
    if (!registrationSettings) return;
    setExternalUrl(value);
    if (value && !validateUrl(value)) {
      return;
    }

    const updatedSettings: Registration = {
      ...registrationSettings,
      externalRegistration: {
        ...registrationSettings?.externalRegistration,
        url: value,
      },
    };

    dispatch(saveWorkingProjectByFields({ registration_settings: updatedSettings }));
  };

  return (
    <Container design="GlobalCont1" borderRadius="12px" padding="16px" className="flex fd-column gap-12">
      <div className="flex ai-center jc-space-between">
        <Text tag="h6" className="H6">
          Disable native registration
        </Text>
        <div>
          <SwitchV2
            loading={!registrationSettings || savingWorkingProject}
            value={!!disableNativeRegistrationIsOn}
            on={!!disableNativeRegistrationIsOn}
            onClick={toggleDisableNativeRegistration}
          />
        </div>
      </div>
      <OptionalComponent display={disableNativeRegistrationIsOn}>
        <InputWithOverlay
          inputProps={{
            type: 'url',
            defaultValue: externalUrl || '',
            placeholder: 'Registration URL',
            disabled: !disableNativeRegistrationIsOn || savingWorkingProject,
            style: {
              backgroundColor: 'var(--global-cont-2)',
            },
            onBlur: (e: ChangeEvent<HTMLInputElement>) => setExernalUrl(e.target.value),
          }}
          inputHeight="36px"
          dirtyError={({ dirty, setHasError }) => {
            setHasError(dirty && !!(!externalUrl || !validateUrl(externalUrl)));
          }}
          isBorderless={true}
        />
      </OptionalComponent>
    </Container>
  );
}
